import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Custom hook to manage tab state in URL search params.
 * This allows tab state to persist across navigation and enables sharing URLs with specific tabs selected.
 *
 * @param defaultTab - The default tab value if none is present in URL (defaults to "before")
 * @returns An object with the current tab value and a setter function
 *
 * @example
 * ```tsx
 * const { tab, setTab } = useTabSearchParams("overview");
 *
 * <Tabs value={tab} onValueChange={setTab}>
 *   <TabsList>
 *     <TabsTrigger value="overview">Overview</TabsTrigger>
 *     <TabsTrigger value="details">Details</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="overview">...</TabsContent>
 *   <TabsContent value="details">...</TabsContent>
 * </Tabs>
 * ```
 */
export const useTabSearchParams = (defaultTab = "before") => {
    const [searchParams, setSearchParams] = useSearchParams();

    const tab = searchParams.get("tab") || defaultTab;

    const setTab = useCallback(
        (newTab: string) => {
            searchParams.set("tab", newTab);
            setSearchParams(searchParams, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    return { tab, setTab };
};
